import { Component, Input, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ScormModule, SectionContent, SectionContentSession, ScormProvider } from '@stuplay';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../../../utils/components/toast/toast.service';
import { ActivityExitCoordinator } from '../../../utils/services/activity-exit.coordinator';

/**
 * idle      → tarjeta con el botón de play (estado inicial, en pestaña nueva o tras salir sin poder avanzar)
 * launching → pidiendo la URL de lanzamiento a la API
 * playing   → modal a pantalla completa con el SCORM
 * syncing   → el alumno salió: se espera su progreso y se avanza a la siguiente actividad
 */
type ScormCloudState = 'idle' | 'launching' | 'playing' | 'syncing';

@Component({
    selector: 'app-scorm-cloud',
    templateUrl: './scorm-cloud.component.html',
})
export class ScormCloudComponent implements OnInit, OnDestroy {
    @Input() scorm: ScormModule;
    @ViewChild('modalFullScreen', { static: true }) modalFullScreen: TemplateRef<any>;

    public sectionContent: SectionContent;
    public sectionContentSession: SectionContentSession;
    public preview: boolean = false;
    public state: ScormCloudState = 'idle';
    public url: SafeResourceUrl;
    public iframeLoaded: boolean = false;

    private modalRef: NgbModalRef;
    private iframeWindow: Window = null;
    private launchSubscription: Subscription;
    private exitSubscription: Subscription;
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private scormProvider: ScormProvider,
        private sanitizer: DomSanitizer,
        private toastService: ToastService,
        private modalService: NgbModal,
        private activityExitCoordinator: ActivityExitCoordinator,
        private ngZone: NgZone,
    ) { }

    ngOnInit() {
        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
            this.preview = !!params['preview'];
        });

        // El componente se reutiliza al pasar de un SCORM a otro: se recarga en cada cambio de ruta.
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(() => this.load());

        window.addEventListener('message', this.onWindowMessage);
    }

    ngOnDestroy() {
        window.removeEventListener('message', this.onWindowMessage);
        this.reset();
        this.destroy$.next();
        this.destroy$.complete();
    }

    isReady(): boolean {
        return !!this.scorm && (!this.scorm.upload || this.scorm.upload?.status === 'done');
    }

    launch(): void {
        if (this.state !== 'idle' || !this.isReady()) {
            return;
        }

        this.state = 'launching';
        // La pestaña se abre dentro del clic: abrirla al volver la petición la bloquea el navegador.
        const newTab = this.scorm.displayType === 'new_tab' ? window.open('', '_blank') : null;

        this.launchSubscription = this.scormProvider.launchScorm(this.scorm.id, this.sectionContent?.id, this.preview, !newTab).subscribe((data) => {
            if (newTab) {
                newTab.location.href = data.url;
                this.state = 'idle';
                return;
            }

            this.url = this.sanitizer.bypassSecurityTrustResourceUrl(data.url);
            this.openPlayer();
        }, () => {
            newTab?.close();
            this.state = 'idle';
            this.toastService.push('error-occurred', 'error');
        });
    }

    onIframeLoad(event: Event): void {
        this.iframeWindow = (event.target as HTMLIFrameElement)?.contentWindow || null;
        this.iframeLoaded = true;
    }

    /**
     * assets/scorm-exit.html avisa cuando el alumno usa el botón de salida del propio paquete.
     * Solo cuenta si viene del iframe del SCORM o de un frame dentro de él (se sube por
     * `parent`, que el navegador permite leer entre orígenes distintos).
     */
    private onWindowMessage = (event: MessageEvent): void => {
        if (event?.data?.type !== 'msc:scorm-exit' || this.state !== 'playing' || !this.isFromScormIframe(event.source)) {
            return;
        }

        this.ngZone.run(() => this.modalRef?.close('scorm-exit'));
    };

    private isFromScormIframe(source: MessageEventSource | null): boolean {
        let current = source as Window;

        for (let depth = 0; current && this.iframeWindow && depth < 10; depth++) {
            if (current === this.iframeWindow) {
                return true;
            }
            if (current.parent === current) {
                return false;
            }
            current = current.parent;
        }

        return false;
    }

    private load(): void {
        this.reset();
        this.sectionContent = this.route.snapshot.data?.scormcloud;
        this.scorm = this.sectionContent?.scormcloud;
        this.sectionContentSession = this.route.snapshot.data?.sectionContentSession;

        // En pestaña nueva no se abre solo: sin un clic del alumno el navegador bloquea la ventana.
        if (this.scorm?.displayType !== 'new_tab') {
            this.launch();
        }
    }

    private openPlayer(): void {
        const options: NgbModalOptions = {
            windowClass: 'force-fullscreen-lesson-media',
            backdropClass: 'backdrop-custom',
            // Solo se sale con la X: un clic fuera o ESC no deben sacar al alumno del paquete.
            backdrop: 'static',
            keyboard: false,
        };

        this.iframeLoaded = false;
        this.state = 'playing';

        const modalRef = this.modalService.open(this.modalFullScreen, options);
        this.modalRef = modalRef;
        modalRef.result.then(() => this.onPlayerClosed(modalRef), () => this.onPlayerClosed(modalRef));
    }

    private onPlayerClosed(modalRef: NgbModalRef): void {
        // Un cierre hecho por reset() (cambio de ruta) no es una salida del alumno.
        if (this.modalRef !== modalRef) {
            return;
        }

        this.modalRef = null;
        this.url = null;
        this.state = 'syncing';

        this.exitSubscription = this.activityExitCoordinator
            .exitAndAdvance(this.sectionContentSession, this.preview)
            .subscribe((navigated) => {
                if (!navigated) {
                    this.state = 'idle';
                }
            }, () => {
                this.state = 'idle';
            });
    }

    private reset(): void {
        this.launchSubscription?.unsubscribe();
        this.exitSubscription?.unsubscribe();

        const modalRef = this.modalRef;
        this.modalRef = null;
        modalRef?.dismiss('reset');

        this.url = null;
        this.iframeWindow = null;
        this.iframeLoaded = false;
        this.state = 'idle';
    }
}
